export type ErrorReportExclusionReason = 'browser_extension_error' | 'third_party_domain_error';

export interface ErrorReportStackframeInput {
    file?: string;
    inProject?: boolean;
}

export interface ErrorReportFilterInput {
    errorClass?: string;
    errorMessage?: string;
    requestUrl?: string;
    stacktrace?: Array<ErrorReportStackframeInput>;
}

export interface ErrorReportFilterDecision {
    shouldReport: boolean;
    reason?: ErrorReportExclusionReason;
    matchedUrl?: string;
    matchedHost?: string;
}

export interface ErrorReportFilterPolicyConfig {
    firstPartyDomainSuffixes: Array<string>;
    criticalThirdPartyDomainSuffixes?: Array<string>;
    extensionProtocols?: Array<string>;
}

const DEFAULT_EXTENSION_PROTOCOLS: Array<string> = [
    'chrome-extension:',
    'moz-extension:',
    'safari-extension:',
    'edge-extension:',
];
const URL_PATTERN: RegExp =
    /(https?:\/\/[^\s"'`<>]+|(?:chrome|moz|safari|edge)-extension:\/\/[^\s"'`<>]+)/gi;

export class ErrorReportFilterPolicyService {
    private readonly firstPartyDomainSuffixes: Array<string>;
    private readonly criticalThirdPartyDomainSuffixes: Array<string>;
    private readonly extensionProtocols: Array<string>;

    public constructor(config: ErrorReportFilterPolicyConfig) {
        this.firstPartyDomainSuffixes = config.firstPartyDomainSuffixes
            .map((suffix) => suffix.trim().toLowerCase())
            .filter(Boolean);
        this.criticalThirdPartyDomainSuffixes = (config.criticalThirdPartyDomainSuffixes ?? [])
            .map((suffix) => suffix.trim().toLowerCase())
            .filter(Boolean);
        this.extensionProtocols = (config.extensionProtocols ?? DEFAULT_EXTENSION_PROTOCOLS)
            .map((protocol) => protocol.trim().toLowerCase())
            .filter(Boolean);
    }

    public evaluate(input: ErrorReportFilterInput): ErrorReportFilterDecision {
        const collectedUrls: Array<string> = this.collectUrls(input);
        const extensionMatch: string | undefined = collectedUrls.find((url) =>
            this.isExtensionUrl(url),
        );

        if (extensionMatch) {
            return {
                shouldReport: false,
                reason: 'browser_extension_error',
                matchedUrl: extensionMatch,
            };
        }

        if (this.hasInProjectFrames(input)) {
            return { shouldReport: true };
        }

        const hosts: Array<string> = this.extractHosts(collectedUrls);
        if (hosts.length === 0) {
            return { shouldReport: true };
        }

        const firstPartyHosts: Array<string> = hosts.filter((host) => this.isFirstPartyHost(host));
        if (firstPartyHosts.length > 0) {
            return { shouldReport: true };
        }

        const criticalThirdPartyHosts: Array<string> = hosts.filter((host) =>
            this.isCriticalThirdPartyHost(host),
        );
        if (criticalThirdPartyHosts.length > 0) {
            return { shouldReport: true };
        }

        return {
            shouldReport: false,
            reason: 'third_party_domain_error',
            matchedHost: hosts[0],
        };
    }

    private hasInProjectFrames(input: ErrorReportFilterInput): boolean {
        return (input.stacktrace ?? []).some((stackframe) => stackframe.inProject === true);
    }

    private collectUrls(input: ErrorReportFilterInput): Array<string> {
        const candidates: Array<string | undefined> = [
            input.errorMessage,
            input.errorClass,
            input.requestUrl,
            ...(input.stacktrace ?? []).map((stackframe) => stackframe.file),
        ];
        const urls: Set<string> = new Set<string>();

        candidates.forEach((candidate) => {
            if (typeof candidate !== 'string' || !candidate.trim()) {
                return;
            }

            const matches: RegExpMatchArray | null = candidate.match(URL_PATTERN);
            if (!matches) {
                return;
            }

            matches.forEach((match) => {
                const normalized: string = this.normalizeUrl(match);
                if (normalized) {
                    urls.add(normalized);
                }
            });
        });

        return Array.from(urls);
    }

    private normalizeUrl(rawUrl: string): string {
        return rawUrl.replace(/[),.;]+$/g, '').trim();
    }

    private isExtensionUrl(url: string): boolean {
        const normalizedUrl: string = url.trim().toLowerCase();

        return this.extensionProtocols.some((protocol) => normalizedUrl.startsWith(protocol));
    }

    private extractHosts(urls: Array<string>): Array<string> {
        const hosts: Set<string> = new Set<string>();

        urls.forEach((url) => {
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
                return;
            }

            try {
                const parsedUrl: URL = new URL(url);
                if (parsedUrl.hostname) {
                    hosts.add(parsedUrl.hostname.toLowerCase());
                }
            } catch {
                return;
            }
        });

        return Array.from(hosts);
    }

    private isFirstPartyHost(host: string): boolean {
        return this.firstPartyDomainSuffixes.some(
            (suffix) => host === suffix || host.endsWith(`.${suffix}`),
        );
    }

    private isCriticalThirdPartyHost(host: string): boolean {
        return this.criticalThirdPartyDomainSuffixes.some(
            (suffix) => host === suffix || host.endsWith(`.${suffix}`),
        );
    }
}
